/**
 * UserOffers Component
 *
 * Displays offers for a user with expandable rows showing reward limits.
 * Each offer row can be expanded to show monthly/annual limit usage.
 * Pattern follows OfferList expandable rows implementation.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssignmentStrategyBadge, RewardVariantBadge } from '@/components/common/Badge';
import { ActionMenu } from '@/components/common/ActionMenu';
import { AssignOfferModal } from '../AssignOfferModal';
import { offerService } from '@/app/services/offerService';
import type { OfferResponse } from '@/app/types/offers';
import type { RewardLimitStatus } from '@/app/types/user';
import { logger } from '@/app/utils/logger';
import './UserOffers.scss';

interface UserOffersProps {
  offers: OfferResponse[];
  rewardLimits: RewardLimitStatus[];
  userId: number;
  partnerProductId?: number | null;
  onAssign: () => void;
  onRemove: (offerId: string) => void;
}

/**
 * OfferLimitsDisplay - Shows monthly/annual limit usage with progress bars
 */
const OfferLimitsDisplay: React.FC<{ limits: RewardLimitStatus }> = ({ limits }) => {
  // API returns Decimal as string, convert to number for display
  const toNum = (val: number | string): number => {
    if (typeof val === 'string') return parseFloat(val) || 0;
    return val || 0;
  };

  const formatValue = (value: number | string, type: string) => {
    const numVal = toNum(value);
    if (type === 'count') {
      return numVal.toString();
    }
    return `$${numVal.toFixed(2)}`;
  };

  const formatResetDate = (isoDate: string) => {
    // Parse as UTC to avoid timezone shifting issues
    const resetDate = new Date(isoDate);
    const now = new Date();
    const diffMs = resetDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays <= 7) {
      return `in ${diffDays} days`;
    } else {
      // For dates further out, show the month
      return resetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getProgressClass = (usage: number | string, limit: number | string) => {
    const usageNum = toNum(usage);
    const limitNum = toNum(limit);
    if (limitNum === 0) return '';
    const percentage = (usageNum / limitNum) * 100;
    if (percentage >= 100) return 'danger';
    if (percentage >= 90) return 'warning';
    return '';
  };

  const getProgressWidth = (usage: number | string, limit: number | string) => {
    const usageNum = toNum(usage);
    const limitNum = toNum(limit);
    if (limitNum === 0) return 0;
    return Math.min(100, (usageNum / limitNum) * 100);
  };

  if (limits.limit_type === 'unlimited') {
    return <div className="limits-unlimited">No redemption limits for this reward</div>;
  }

  const hasMonthlyLimit = toNum(limits.monthly_limit) > 0;
  const hasAnnualLimit = toNum(limits.annual_limit) > 0;

  // No limits configured
  if (!hasMonthlyLimit && !hasAnnualLimit) {
    return <div className="limits-unlimited">No redemption limits configured</div>;
  }

  return (
    <div className="limits-grid">
      {/* Monthly Limit */}
      <div className="limit-section">
        <div className="limit-header">
          <span>Monthly Limit</span>
          {hasMonthlyLimit && limits.monthly_hit && (
            <span className="limit-hit-badge">Limit Hit</span>
          )}
        </div>
        {hasMonthlyLimit ? (
          <>
            <div className="limit-progress">
              <div
                className={`progress-bar ${getProgressClass(limits.monthly_usage, limits.monthly_limit)}`}
                style={{
                  width: `${getProgressWidth(limits.monthly_usage, limits.monthly_limit)}%`,
                }}
              />
            </div>
            <div className="limit-values">
              {formatValue(limits.monthly_usage, limits.limit_type)} /{' '}
              {formatValue(limits.monthly_limit, limits.limit_type)}
              <span className="limit-remaining">
                ({formatValue(limits.monthly_remaining, limits.limit_type)} remaining)
              </span>
            </div>
            <div className="limit-reset">Resets {formatResetDate(limits.month_resets_at)}</div>
          </>
        ) : (
          <div className="limit-none">No monthly limit</div>
        )}
      </div>

      {/* Annual Limit */}
      <div className="limit-section">
        <div className="limit-header">
          <span>Annual Limit</span>
          {hasAnnualLimit && limits.annual_hit && (
            <span className="limit-hit-badge">Limit Hit</span>
          )}
        </div>
        {hasAnnualLimit ? (
          <>
            <div className="limit-progress">
              <div
                className={`progress-bar ${getProgressClass(limits.annual_usage, limits.annual_limit)}`}
                style={{ width: `${getProgressWidth(limits.annual_usage, limits.annual_limit)}%` }}
              />
            </div>
            <div className="limit-values">
              {formatValue(limits.annual_usage, limits.limit_type)} /{' '}
              {formatValue(limits.annual_limit, limits.limit_type)}
              <span className="limit-remaining">
                ({formatValue(limits.annual_remaining, limits.limit_type)} remaining)
              </span>
            </div>
            <div className="limit-reset">Resets {formatResetDate(limits.year_resets_at)}</div>
          </>
        ) : (
          <div className="limit-none">No annual limit</div>
        )}
      </div>
    </div>
  );
};

export const UserOffers: React.FC<UserOffersProps> = ({
  offers,
  rewardLimits,
  userId,
  partnerProductId,
  onAssign,
  onRemove,
}) => {
  const navigate = useNavigate();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [removingOfferId, setRemovingOfferId] = useState<string | null>(null);
  const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((offerId: string) => {
    setExpandedOffers((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  }, []);

  // Sort offers by priority (ascending - lower number = higher priority)
  const sortedOffers = useMemo(() => {
    return [...offers].sort((a, b) => (a.display_priority ?? 999) - (b.display_priority ?? 999));
  }, [offers]);

  const handleRemove = useCallback(
    async (offerId: string) => {
      if (!window.confirm('Are you sure you want to remove this offer?')) {
        return;
      }

      setRemovingOfferId(offerId);
      try {
        const success = await offerService.deleteOffer(offerId);
        if (success) {
          onRemove(offerId);
        } else {
          logger.error('Failed to remove offer', { offerId });
          alert('Failed to remove offer');
        }
      } catch (err) {
        logger.error('Failed to remove offer', { error: err, offerId });
        alert('Failed to remove offer');
      } finally {
        setRemovingOfferId(null);
      }
    },
    [onRemove]
  );

  const handleAssignSuccess = useCallback(() => {
    onAssign();
    setIsAssignModalOpen(false);
  }, [onAssign]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleCreateOffer = useCallback(() => {
    const params = new URLSearchParams();
    params.set('userId', userId.toString());
    if (partnerProductId) {
      params.set('partnerProductId', partnerProductId.toString());
    }
    navigate(`/offers/new?${params.toString()}`);
  }, [navigate, userId, partnerProductId]);

  const getLimitsForReward = (rewardId: string): RewardLimitStatus | undefined => {
    return rewardLimits.find((l) => l.reward_id === rewardId);
  };

  return (
    <div className="user-offers">
      <div className="user-offers-header">
        <h3>Active Offers</h3>
        <div className="header-actions">
          <button
            className="user-offers-assign-btn secondary"
            onClick={() => setIsAssignModalOpen(true)}
          >
            Quick Assign
          </button>
          <button className="user-offers-assign-btn" onClick={handleCreateOffer}>
            + Add Offer
          </button>
        </div>
      </div>

      {sortedOffers.length === 0 ? (
        <div className="user-offers-empty">
          <p>No offers assigned to this user.</p>
          <div className="empty-actions">
            <button
              className="user-offers-assign-btn secondary"
              onClick={() => setIsAssignModalOpen(true)}
            >
              Quick Assign
            </button>
            <button className="user-offers-assign-btn" onClick={handleCreateOffer}>
              + Add Offer
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Column Headers */}
          <div className="user-offers-table-header">
            <span className="col-expand"></span>
            <span className="col-name">Reward Name</span>
            <span className="col-variant">Variant</span>
            <span className="col-provider">Provider</span>
            <span className="col-priority">Priority</span>
            <span className="col-strategy">Strategy</span>
            <span className="col-created">Created</span>
            <span className="col-actions">Actions</span>
          </div>
          <div className="user-offers-list">
            {sortedOffers.map((offer) => {
              const isExpanded = expandedOffers.has(offer.offer_id);
              const limits = getLimitsForReward(offer.reward_id);

              return (
                <div key={offer.offer_id} className={`offer-row ${isExpanded ? 'expanded' : ''}`}>
                  {/* Header Row - clickable to expand */}
                  <div className="offer-header" onClick={() => toggleExpand(offer.offer_id)}>
                    <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                    <span className="reward-name">{offer.reward_name || 'Unknown Reward'}</span>
                    <span className="reward-variant">
                      {offer.reward_variant ? (
                        <RewardVariantBadge variant={offer.reward_variant} />
                      ) : (
                        '-'
                      )}
                    </span>
                    <span className="reward-provider">{offer.provider_name || '-'}</span>
                    <span className="priority">{offer.display_priority}</span>
                    <span className="strategy">
                      <AssignmentStrategyBadge strategy={offer.assignment_strategy} />
                    </span>
                    <span className="created-date">{formatDate(offer.created_at)}</span>
                    <div className="actions" onClick={(e) => e.stopPropagation()}>
                      <ActionMenu
                        items={[
                          {
                            label: 'View Details',
                            icon: '👁',
                            onClick: () => navigate(`/offers/${offer.offer_id}`),
                          },
                          {
                            label: 'Remove Offer',
                            icon: '🗑️',
                            variant: 'danger',
                            onClick: () => handleRemove(offer.offer_id),
                            disabled: removingOfferId === offer.offer_id,
                          },
                        ]}
                        triggerLabel="Actions"
                        triggerIcon="▾"
                      />
                    </div>
                  </div>

                  {/* Expanded Detail - limits info */}
                  {isExpanded && limits && (
                    <div className="offer-limits-detail">
                      <OfferLimitsDisplay limits={limits} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <AssignOfferModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={handleAssignSuccess}
        userId={userId}
        partnerProductId={partnerProductId}
      />
    </div>
  );
};

export default UserOffers;
